import React from 'react';
import { flex } from '@fabric-ui/theme';
import { Box, Button, ButtonProps, ButtonTypeMap, IconButton, IconButtonProps } from '@mui/material';
import { AvTooltip, AvTooltipProps, Loading } from './';

export enum ButtonKinds {
  TOGGLE = 'TOGGLE',
  ICON = 'ICON',
}

export enum ButtonPosition {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  CENTER = 'CENTER',
}

type Props<RootComponent extends React.ElementType = ButtonTypeMap['defaultComponent'], AdditionalProps = {}> = ButtonProps<
  RootComponent,
  AdditionalProps
> & {
  kind?: ButtonKinds;
  isActive?: boolean;
  tooltip?: string;
  position?: ButtonPosition;
  toolTipProps?: Omit<AvTooltipProps, 'children'>;
  loading?: boolean;
};

export const AvButton = <RootComponent extends React.ElementType = ButtonTypeMap['defaultComponent'], AdditionalProps = {}>({
  children,
  kind,
  isActive,
  tooltip,
  position,
  toolTipProps,
  loading,
  ...props
}: Props<RootComponent, AdditionalProps>) => {
  // TODO: Figure out if Button is exactly like LoadingButton when `loading=false`
  if (loading !== undefined) {
    return (
      <Button
        {...props}
        loading={loading}
        loadingIndicator={<Loading height={20} />}
        sx={theme => ({
          '&.MuiButton-loading': { '&, svg': { color: 'transparent' } },
          ...(typeof props.sx === 'function' ? props.sx(theme) : props.sx),
        })}>
        {children}
      </Button>
    );
  }

  if (kind === ButtonKinds.TOGGLE) {
    return (
      <Button
        variant="filter"
        {...props}
        sx={theme => ({
          height: '24px',
          padding: '3px 8px',
          borderRadius:
            position === ButtonPosition.LEFT ? '10px 0px 0px 10px' : position === ButtonPosition.RIGHT ? '0px 10px 10px 0px' : '0px',
          ...(typeof props.sx === 'function' ? props.sx(theme) : props.sx),
          ...(isActive && { backgroundColor: 'var(--color-surface-interactive-modifier-hover)' }),
        })}>
        <AvTooltip title={tooltip}>
          <Box sx={{ ...flex.col, fontSize: 12, lineHeight: 1 }}>{children}</Box>
        </AvTooltip>
      </Button>
    );
  }

  if (kind === ButtonKinds.ICON) {
    const iconButton = <IconButton {...(props as IconButtonProps)}>{children}</IconButton>;
    return tooltip ? (
      <AvTooltip title={tooltip} {...toolTipProps}>
        {iconButton}
      </AvTooltip>
    ) : (
      iconButton
    );
  }

  const button = <Button {...props}>{children}</Button>;
  return tooltip ? (
    <AvTooltip title={tooltip} {...toolTipProps}>
      <div>{button}</div>
    </AvTooltip>
  ) : (
    button
  );
};
